import { forwardRef, useMemo } from "react";

import { BoxProps } from "@mui/material/Box";
import Divider, { DividerProps } from "@mui/material/Divider";
import { useTheme } from "@mui/material/styles";

import { ZeffyColorTokenBorderClasses } from "@/dashboard-design-system/helpers";
import { mergeSx } from "@/helpers/mergeSx";
import { ZeffyColorTokenBorder, ZeffyPaletteIntensity } from "@/types/mui";

export type DashboardDividerProps = Omit<DividerProps, "vibe"> & {
  vibe?: ZeffyColorTokenBorderClasses | "currentColor";
  borderColor?: BoxProps["borderColor"];
};

export const DashboardDivider = forwardRef<HTMLHRElement, DashboardDividerProps>(
  ({ vibe = "border-neutral-quiet", sx, borderColor, ...rest }, ref) => {
    const theme = useTheme();

    const vibeColor = useMemo(() => {
      if (vibe === "currentColor") {
        return vibe;
      }

      if (vibe.startsWith("border-")) {
        const typedVibe = vibe as ZeffyColorTokenBorderClasses;
        const [, palette, intensity] = typedVibe.split("-");

        if (typeof theme.palette.border[palette as keyof ZeffyColorTokenBorder] === "string") {
          // Legacy token detected
          return theme.palette.border[palette as keyof ZeffyColorTokenBorder];
        }

        const typedPalette = palette as keyof ZeffyColorTokenBorder;
        const typedIntensity = intensity as keyof ZeffyPaletteIntensity;

        if (!typedPalette || !typedIntensity) {
          throw new Error(`Invalid vibe: ${vibe}`);
        }

        const paletteValue = theme.palette.border[typedPalette] as ZeffyPaletteIntensity | undefined;
        const intensityValue = paletteValue?.[typedIntensity] as string | undefined;

        if (!intensityValue) {
          throw new Error(`Invalid vibe: ${vibe}`);
        }

        return intensityValue;
      }

      throw new Error(`Invalid vibe: ${vibe}`);
    }, [vibe, theme.palette.border]);

    const styles = mergeSx(
      {
        ["&, &:before, &:after"]: {
          color: borderColor ?? vibeColor,
          borderColor: borderColor ?? vibeColor,
        },
      },
      sx
    );

    return <Divider ref={ref} sx={styles} {...rest} />;
  }
);

DashboardDivider.displayName = "DashboardDivider";
